import { useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { parseBatchResponse } from '../../procurement/parseBatchResponse'
import { buildUploadSummary } from '../../procurement/uploadSummary'

type RequestItem = {
  id: string
  partNumber: string
  matrix?: string | null
}

type RequestLike =
  | {
      items?: RequestItem[]
    }
  | undefined

// Загрузка ответа поставщика из карточки заявки. Логика парсинга, выбор
// ветки (новый procurement-request по subject vs старый bulk) живёт в
// общем parseBatchResponse — мы только адаптируем items карточки к нужному
// формату и обновляем UI/кэш. Раньше тут был самостоятельный парсер
// со старым набором колонок (non-batch), и при включении isBatch:true
// в шаблоне колонки разъехались — все строки шли в NaN, 0 квот.
export function useUploadResponse(request: RequestLike, requestId: string | undefined) {
  const queryClient = useQueryClient()
  const uploadRef = useRef<HTMLInputElement>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadResult, setUploadResult] = useState<string | null>(null)

  const handleUploadResponse = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !request) return
    e.target.value = ''

    setIsUploading(true)
    setUploadResult(null)
    try {
      const items = (request.items ?? []).map((i) => ({
        requestItemId: i.id,
        partNumber: i.partNumber,
        matrix: i.matrix ?? null,
      }))
      const result = await parseBatchResponse(file, items)
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['requests', requestId] }),
        queryClient.invalidateQueries({ queryKey: ['procurement-requests'] }),
      ])
      setUploadResult(buildUploadSummary(result))
      // Если есть битые строки — держим тост дольше, чтобы успеть прочитать.
      setTimeout(() => setUploadResult(null), result.invalidRows.length > 0 ? 12000 : 6000)
    } catch (err) {
      // Сервер возвращает русские тексты в err.response.data.error.message
      // (через AppError → ErrorHandler). axios без явного парсинга кладёт
      // в err.message только «Request failed with status code N» — это
      // непонятно. Пытаемся сначала достать серверный текст.
      const e = err as {
        response?: { data?: { error?: { message?: string } } }
        message?: string
      }
      const msg =
        e?.response?.data?.error?.message || (err instanceof Error ? err.message : String(err))
      setUploadResult('Ошибка: ' + msg)
      setTimeout(() => setUploadResult(null), 8000)
    } finally {
      setIsUploading(false)
    }
  }

  return { uploadRef, isUploading, uploadResult, handleUploadResponse }
}
